import type { Blog } from '@/payload-types'
import Link from 'next/link'
import { ArrowRightIcon, Calendar, Clock, Tag } from 'lucide-react'
import { getServerSideURL } from '@/utilities/getURL'

type Props = {
  blog: Blog
}

const categoryLabels: Record<string, string> = {
  voeding: 'Voeding',
  fitness: 'Fitness',
  leefstijl: 'Leefstijl',
  recepten: 'Recepten',
  tips: 'Tips',
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return null
  const date = new Date(dateString)
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const BlogCard = ({ blog }: Props) => {
  const { title, slug, intro, image, category, publishedAt, readingTime, author } = blog

  type ImageSize = {
    url?: string | null
    filename?: string | null
    width?: number | null
    height?: number | null
  }

  type UploadedLike = {
    url?: string | null
    filename?: string | null
    alt?: string | null
    focalX?: number | null
    focalY?: number | null
    sizes?: {
      thumbnail?: ImageSize | null
      card?: ImageSize | null
      large?: ImageSize | null
    } | null
  }

  const media = typeof image === 'object' ? (image as UploadedLike) : null
  const serverURL = getServerSideURL()
  const cardSize = media?.sizes?.card
  const imageURL = cardSize?.url
    ? `${serverURL}${cardSize.url}`
    : cardSize?.filename
      ? `${serverURL}/media/${cardSize.filename}`
      : media?.url
        ? `${serverURL}${media.url}`
        : media?.filename
          ? `${serverURL}/media/${media.filename}`
          : null
  const focalX = typeof media?.focalX === 'number' ? media?.focalX : 50
  const focalY = typeof media?.focalY === 'number' ? media?.focalY : 50

  const formattedDate = formatDate(publishedAt)

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border/50 bg-card shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5">
      {/* Image Section */}
      {imageURL ? (
        <Link href={`/blog/${slug}`} className="relative aspect-[16/10] overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt={media?.alt || title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            src={imageURL}
            style={{ objectPosition: `${focalX}% ${focalY}%` }}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

          {/* Category badge on image */}
          {category ? (
            <div className="absolute left-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-xs font-semibold text-primary-foreground shadow-lg backdrop-blur-sm">
              {categoryLabels[category] || category}
            </div>
          ) : null}
        </Link>
      ) : null}

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-6">
        {/* Meta info */}
        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {formattedDate && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formattedDate}</span>
            </div>
          )}
          {readingTime && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span>{readingTime} min leestijd</span>
            </div>
          )}
        </div>

        {/* Title */}
        <Link href={`/blog/${slug}`} className="group/title mb-3">
          <h3 className="text-xl font-bold leading-snug text-foreground transition-colors group-hover/title:text-primary">
            {title}
          </h3>
        </Link>

        {/* Intro */}
        {intro ? (
          <p className="mb-4 line-clamp-2 flex-1 text-sm leading-relaxed text-muted-foreground">
            {intro}
          </p>
        ) : null}

        {/* Author */}
        {author && (
          <p className="mb-4 text-xs text-muted-foreground">
            Door <span className="font-medium text-foreground">{author}</span>
          </p>
        )}

        {/* CTA */}
        <div className="mt-auto">
          <Link
            href={`/blog/${slug}`}
            className="group/btn inline-flex items-center gap-2 rounded-full bg-primary/10 px-5 py-2.5 text-sm font-semibold text-primary transition-all hover:bg-primary hover:text-primary-foreground"
          >
            <span>Lees meer</span>
            <ArrowRightIcon
              aria-hidden
              className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5"
            />
          </Link>
        </div>
      </div>
    </article>
  )
}
